import React from 'react';
import { ColumnFilter, FilterField } from '../FilterToolbar';
import './DataList.css';

export type HeaderColumn = {
    label: string;
    /** stands for the column, once, instead of on every row beneath it */
    icon?: React.ReactNode;
    /** the filters this column opens — omit for a plain, unfilterable label */
    fields?: FilterField[];
    align?: 'left' | 'right';
    dataCy?: string;
};

/**
 * One header row for a list, where every column doubles as its own filter.
 *
 * It replaces labels repeated inside every row, and it is where those filters
 * live: a page that shows this marks them `renderedElsewhere` so the filter
 * panel does not offer them a second time.
 *
 * Layout is the caller's: pass `template` to line the headers up with the
 * columns below (a CSS variable shared with the rows keeps the two from
 * drifting), or `flow` where there are no columns to sit over and the headers
 * should wrap into a plain bar of filters instead.
 */
const ColumnHeader = ({
    columns,
    schema,
    values,
    defaults,
    onChange,
    template,
    flow,
    trailing,
    className,
    dataCy,
}: {
    columns: HeaderColumn[];
    /** the page's whole filter schema, for cascade clearing */
    schema: FilterField[];
    values: Record<string, any>;
    /** what the list filters by before anyone touches it */
    defaults?: Record<string, any>;
    onChange: (next: Record<string, any>) => void;
    /** grid-template-columns for the header cells, e.g. "var(--si-cols)" */
    template?: string;
    /** wrap the headers into a bar rather than standing them over columns */
    flow?: boolean;
    /** trailing cell, for a column the rows use but the header cannot name */
    trailing?: React.ReactNode;
    className?: string;
    dataCy?: string;
}) => (
    <div
        className={`dl-head${flow ? ' dl-head--flow' : ''}${className ? ` ${className}` : ''}`}
        style={{ gridTemplateColumns: template }}
        data-cy={dataCy}
    >
        {columns.map((column) => (
            <ColumnFilter
                key={column.label}
                label={column.label}
                icon={column.icon}
                fields={column.fields ?? []}
                schema={schema}
                values={values}
                defaults={defaults}
                onChange={onChange}
                align={column.align}
                dataCy={column.dataCy}
            />
        ))}
        {!flow && trailing}
    </div>
);

export default ColumnHeader;
